import React from 'react';

/**
 * The Vibana mark — "The Seam".
 *
 * A calligraphic V: heavy down-stroke, lighter up-stroke, echoing the broad-nib
 * contrast already in Fraunces. The gap between the two strokes is the gold seam
 * that runs through the rest of the interface.
 *
 * Inline SVG rather than `<img src="/vibana-mark.png">`, which is what every
 * header, footer and dashboard used before. Three reasons, in order of how much
 * they cost:
 *
 *   - The PNG was 256px. It was soft on a retina header at 32px and could never
 *     be sharp on a shirt, a poster or a stamp.
 *   - An <img> is a network request and a layout shift on every page load. This
 *     paints with the first byte of HTML.
 *   - The single-ink variant needs a different file as a raster. Here it is a
 *     prop, so a dark background, an embroidery file and a fax all come from
 *     one source.
 *
 * The artwork is duplicated from public/vibana-mark.svg because a Create React
 * App build cannot import an SVG as markup without ejecting. vibanaMark.test.js
 * reads that file and fails if the two stop matching.
 */

/** The two strokes. Identical to the paths in public/vibana-mark.svg. */
export const MARK_PATHS = Object.freeze({
  down: 'M14 20 L34 20 L52 68 L44 82 Z',
  up: 'M72 20 L86 20 L54 82 L46 66 Z',
});

/** Gold leaf, the same ramp as --sunset in the stylesheet. */
export const LEAF_STOPS = Object.freeze([
  { offset: '0', color: '#B8860B' },
  { offset: '.55', color: '#E9A23B' },
  { offset: '1', color: '#F3D27A' },
]);

/** Opacity of the lighter up-stroke, in gold and in one ink. */
const UP_OPACITY = { leaf: 0.72, ink: 0.62 };

let gradientSeq = 0;

/**
 * @param {number} size      rendered px, square
 * @param {'leaf'|'ink'} variant  gold gradient, or a single flat colour
 * @param {string} color     the ink colour when variant is 'ink'
 * @param {string} title     accessible name; pass '' for a decorative mark
 */
export default function VibanaMark({
  size = 28,
  variant = 'leaf',
  color = '#FBF4E6',
  title = 'Vibana',
  style,
  ...rest
}) {
  // A gradient id must be unique per document, not per component: two marks on
  // one page sharing an id makes the second one reference the first's gradient,
  // which is fine until one of them unmounts and takes the definition with it.
  const gradientId = React.useMemo(() => `vibana-leaf-${(gradientSeq += 1)}`, []);
  const isLeaf = variant === 'leaf';
  const fill = isLeaf ? `url(#${gradientId})` : color;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : 'true'}
      focusable="false"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...style }}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {isLeaf && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            {LEAF_STOPS.map((s) => (
              <stop key={s.offset} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>
        </defs>
      )}
      <path d={MARK_PATHS.down} fill={fill} />
      <path d={MARK_PATHS.up} fill={fill} opacity={isLeaf ? UP_OPACITY.leaf : UP_OPACITY.ink} />
    </svg>
  );
}
